import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

/**
 * Authenticate with Google using the ID token from Google Sign-In
 * @param {string} idToken - Google ID token from @react-oauth/google
 * @param {string} existingToken - Optional existing anonymous token for account linking
 * @returns {Promise<Object>} - User data and access token
 */
const googleSignIn = async (idToken, existingToken = null) => {
  try {
    const payload = {
      id_token: idToken,
    };

    // Include existing token for account linking
    if (existingToken) {
      payload.existing_token = existingToken;
    }

    const response = await axios.post(
      `${API_BASE_URL}/api/auth/google`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('[AuthService] Google sign-in error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Create an anonymous token for unauthenticated users
 * @returns {Promise<Object>} - Anonymous token data
 */
const createAnonymousToken = async () => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/create-token`);
    return response.data;
  } catch (error) {
    console.error('[AuthService] Error creating anonymous token:', error);
    throw error;
  }
};

/**
 * Sign up with email and password
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @param {string} name - User's display name
 * @param {string} existingToken - Optional existing anonymous token for account linking
 * @returns {Promise<Object>} - User data and access token
 */
const signup = async (email, password, name, existingToken = null) => {
  try {
    const payload = {
      email,
      password,
      name,
    };

    if (existingToken) {
      payload.existing_token = existingToken;
    }

    const response = await axios.post(
      `${API_BASE_URL}/api/auth/signup`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('[AuthService] Signup error:', error);
    if (error.response?.data) {
      // Create error with full response data for handling unverified accounts
      const err = new Error(error.response.data.message || 'Signup failed');
      err.data = error.response.data.data;
      throw err;
    }
    throw error;
  }
};

/**
 * Login with email and password
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @param {string} existingToken - Optional existing anonymous token for job migration
 * @returns {Promise<Object>} - User data and access token
 */
const login = async (email, password, existingToken = null) => {
  try {
    const payload = {
      email,
      password,
    };

    if (existingToken) {
      payload.existing_token = existingToken;
    }

    const response = await axios.post(
      `${API_BASE_URL}/api/auth/login`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('[AuthService] Login error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Verify email with token
 * @param {string} token - Verification token from email link
 * @returns {Promise<Object>} - Verification result with optional access token
 */
const verifyEmail = async (token) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/auth/verify-email`,
      {
        params: { token },
      }
    );
    return response.data;
  } catch (error) {
    console.error('[AuthService] Verify email error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Resend verification email
 * @param {string} email - User's email address
 * @returns {Promise<Object>} - Result message
 */
const resendVerification = async (email) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/resend-verification`,
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('[AuthService] Resend verification error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Change password for authenticated user
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} - Result message
 */
const changePassword = async (currentPassword, newPassword, token) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/change-password`,
      {
        current_password: currentPassword,
        new_password: newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('[AuthService] Change password error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Request password reset email
 * @param {string} email - User's email address
 * @returns {Promise<Object>} - Result message
 */
const forgotPassword = async (email) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/forgot-password`,
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('[AuthService] Forgot password error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Reset password using token from email
 * @param {string} token - Reset token from email link
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} - User data and access token
 */
const resetPassword = async (token, newPassword) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/reset-password`,
      {
        token,
        new_password: newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('[AuthService] Reset password error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Update user profile
 * @param {Object} profileData - Fields to update (e.g. { name: "New Name" })
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} - Updated profile data
 */
const updateProfile = async (profileData, token) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/update-profile`,
      profileData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('[AuthService] Update profile error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Get user info from stored token (for session restoration)
 * Note: This decodes the JWT client-side - the server validates on each request
 */
const getUserFromToken = (token) => {
  if (!token) return null;

  try {
    // Decode JWT payload (base64)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      userId: payload.sub || payload.identity,
      exp: payload.exp,
    };
  } catch (error) {
    console.error('[AuthService] Error decoding token:', error);
    return null;
  }
};

/**
 * Check if a token is valid (not expired)
 */
const isTokenValid = (token) => {
  if (!token || token === 'null') return false;

  const user = getUserFromToken(token);
  if (!user || !user.exp) return false;

  // Check if token is expired (with 5 minute buffer)
  const now = Math.floor(Date.now() / 1000);
  return user.exp > now + 300;
};

const authService = {
  googleSignIn,
  createAnonymousToken,
  signup,
  login,
  verifyEmail,
  resendVerification,
  changePassword,
  updateProfile,
  forgotPassword,
  resetPassword,
  getUserFromToken,
  isTokenValid,
};

export default authService;
